/**
 * FloatingEmmaButton - Contextual Emma access for workflow pages
 * Provides quick access to Emma AI assistant with context-aware help
 */

import React, { useState } from 'react';
import { MessageSquare, X, Brain } from 'lucide-react';
import { EmmaChat } from './EmmaChat';

interface FloatingEmmaButtonProps {
  context?: string;
  prefilledMessage?: string;
  position?: 'bottom-right' | 'bottom-left';
  className?: string;
}

export const FloatingEmmaButton: React.FC<FloatingEmmaButtonProps> = ({
  context,
  prefilledMessage,
  position = 'bottom-right',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const positionClasses = {
    'bottom-right': 'fixed bottom-20 right-6 z-40',
    'bottom-left': 'fixed bottom-20 left-6 z-40'
  };

  return (
    <>
      {/* Floating Emma Chat Panel */}
      {isOpen && (
        <div className={`${
          position === 'bottom-right' ? 'fixed bottom-32 right-6' : 'fixed bottom-32 left-6'
        } z-40 w-96 max-h-[600px] bg-white rounded-lg shadow-2xl border border-gray-200`}>
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex items-center space-x-2">
              <Brain className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-900">Emma AI Assistant</h3>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-gray-200 rounded-full transition-colors"
              aria-label="Close Emma chat"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>
          </div>
          <div className="h-[500px]">
            <EmmaChat 
              initialContext={{ workflow: context || 'general' }}
            />
          </div>
        </div>
      )}

      {/* Floating Action Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`${
          positionClasses[position]
        } ${className} bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full p-4 shadow-2xl transition-all duration-300 transform hover:scale-110 ${
          isOpen ? 'rotate-0' : 'animate-pulse hover:animate-none'
        }`}
        aria-label="Chat with Emma AI assistant"
        title="💡 Need help? Ask Emma!"
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <MessageSquare className="w-6 h-6" />
        )}
      </button>
    </>
  );
};

export default FloatingEmmaButton;
